import { useEffect, useRef, useSyncExternalStore } from "react";

// «Назад» закрывает то, что открыто поверх экрана: просмотр фото, меню,
// модалку. На Android это системная кнопка/жест, и без этого она закрывала
// бы всю переписку вместе с открытым фото.
//
// Только в мобильной версии (html.m-mode): открытие окна кладёт в историю
// запись с тем же адресом (метка `__mb` — сколько таких записей под ней), а
// «Назад» снимает её — и мы закрываем верхнее окно. Закрыли окно сами
// (крестик, выбор пункта) — убираем его запись шагом назад. Роутер этих
// записей не замечает: адрес не меняется, экраны (mobile/navLayers) те же.

interface Layer {
  /** `false` — окно не закрылось (спросило про несохранённое и получило отказ). */
  close: () => boolean | void;
  byBack: boolean;
}

const layers: Layer[] = [];
// Шаг назад за закрытым окном откладываем на тик: StrictMode и
// пере-монтирование сразу открывают его снова — тогда запись переиспользуем.
let pendingBack: number | null = null;
let listening = false;

function depthOf(state: unknown): number {
  const d = (state as { __mb?: unknown } | null)?.__mb;
  return typeof d === "number" ? d : 0;
}

// Счётчик шагов по истории, которые видит этот модуль: снятая запись окна
// возвращает тот же адрес, и роутер ничего не меняет — а тем, кто пропустил
// запись в адрес (overlayOnTop), надо знать, что теперь можно.
let popGen = 0;
const genListeners = new Set<() => void>();

function onPop() {
  const d = depthOf(window.history.state);
  while (layers.length > d) {
    const top = layers.pop()!;
    top.byBack = true;
    if (top.close() === false) {
      // Окно осталось на экране, а его запись «Назад» уже снял. Возвращаем и
      // запись, и слой: иначе следующий «Назад» достался бы окну под ним, а
      // это так и висело бы поверх без своей записи.
      top.byBack = false;
      layers.push(top);
      const state = (window.history.state ?? {}) as Record<string, unknown>;
      window.history.pushState({ ...state, __mb: layers.length }, "");
      break;
    }
  }
  popGen += 1;
  genListeners.forEach((l) => l());
}

function subscribeGen(l: () => void) {
  genListeners.add(l);
  return () => {
    genListeners.delete(l);
  };
}

/** Меняется, когда по истории шагнули (в том числе сняли запись окна). */
export function useHistoryPops(): number {
  return useSyncExternalStore(subscribeGen, () => popGen, () => popGen);
}

function mobileMode(): boolean {
  return document.documentElement.classList.contains("m-mode");
}

/** Пока компонент смонтирован (или пока `open`), «Назад» зовёт `onClose`.
 *  Окно, которое может не закрыться (несохранённые правки), отвечает `false`
 *  — тогда оно остаётся верхним, и следующий «Назад» снова придёт к нему. */
export function useBackClose(onClose: () => boolean | void, open = true) {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    if (!open || !mobileMode()) return;
    if (!listening) {
      window.addEventListener("popstate", onPop);
      listening = true;
    }
    const layer: Layer = { close: () => ref.current(), byBack: false };
    if (pendingBack !== null) {
      // Запись только что закрытого окна ещё в истории — она и будет этой.
      window.clearTimeout(pendingBack);
      pendingBack = null;
    } else {
      const state = (window.history.state ?? {}) as Record<string, unknown>;
      window.history.pushState({ ...state, __mb: layers.length + 1 }, "");
    }
    layers.push(layer);
    return () => {
      const i = layers.indexOf(layer);
      if (i >= 0) layers.splice(i, 1);
      if (layer.byBack) return;
      if (pendingBack !== null) window.clearTimeout(pendingBack);
      pendingBack = window.setTimeout(() => {
        pendingBack = null;
        const extra = depthOf(window.history.state) - layers.length;
        if (extra > 0) window.history.go(-extra);
      }, 0);
    };
  }, [open]);
}

/** Экран закрывается шагом назад, а над его записью ещё лежат записи окон
 *  (окно закрыли тем же нажатием — «Написать» в модалке закрывает и карточку
 *  человека). Шагнуть надо сразу за все: возвращает, сколько их. Окна, что
 *  ещё открыты, закроются, когда «Назад» дойдёт (onPop); свои записи сами они
 *  уже не снимают. */
export function overlayEntries(): number {
  if (pendingBack !== null) {
    window.clearTimeout(pendingBack);
    pendingBack = null;
  }
  for (const l of layers) l.byBack = true;
  return depthOf(window.history.state);
}

/** Сверху — запись окна (открытого или только что закрытого, чью запись ещё
 *  не сняли). Писать в адрес сейчас нельзя: замена легла бы в запись окна, и
 *  шаг назад за окном откатил бы её. Кто пишет в адрес, пропускает ход — адрес
 *  сменится, когда запись снимут, и он запишет снова. */
export function overlayOnTop(): boolean {
  return typeof window !== "undefined" && depthOf(window.history.state) > 0;
}

/** Уйти на другой экран новой записью, когда сверху ещё записи окон (окно
 *  закрыли тем же нажатием — «Создать» в модалке): сначала снять их — шагом
 *  назад за все разом, — и уже тогда `go`. Иначе новая запись легла бы над
 *  записью окна, и «Назад» с нового экрана вернул бы в неё — в пустой шаг. */
export function afterOverlays(go: () => void) {
  const n = overlayEntries();
  if (n <= 0) {
    go();
    return;
  }
  window.addEventListener("popstate", () => go(), { once: true });
  window.history.go(-n);
}
